import { useState, useEffect } from "react";
import { checkpoints } from "../constants";
import { useColors } from "../ColorsContext";
import { ShareIcon } from "../components/RecapCards";

// Deterministic QR-like pattern (no Math.random → no flicker on re-render)
const QR_CELLS = Array.from({ length: 36 }, (_, i) =>
  [1,0,1,1,0,1,0,1,1,0,0,1,1,0,1,0,1,0,1,1,0,0,1,0,1,1,0,1,0,1,1,0,1,0,0,1][i]
);

function fmt(s) {
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}

export default function ScreenFinale({ path, xpEarned, allyResults, gearUnlocked, splitTimes, onRestart, onShare, layout }) {
  const C = useColors();
  const { scale = 1, offsetX = 0, vpWidth = 390 } = layout || {};
  const extraLeft = offsetX / scale;
  const edgeStyle = {
    marginLeft: -extraLeft, width: vpWidth / scale,
    paddingLeft: extraLeft + 24, paddingRight: extraLeft + 24,
  };
  const [showNext, setShowNext] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShowNext(true), 800); return () => clearTimeout(t); }, []);

  const savedCount = allyResults ? allyResults.filter(Boolean).length : 0;
  const totalTime = splitTimes ? splitTimes.reduce((a, b) => a + b, 0) : 0;

  // Build path-specific finale copy
  let headlineEmoji = "🏆";
  let headlineText = "MISSION ACCOMPLISHED!";
  let subtitleText = path.narrative.toUpperCase();
  let summaryText = "";

  if (path.id === "puzzle") {
    summaryText = path.finaleText(checkpoints.filter((_, i) => i < 6).length);
  } else if (path.id === "action") {
    headlineEmoji = savedCount >= 6 ? "🏅" : savedCount >= 4 ? "🏆" : "💔";
    headlineText = savedCount >= 6 ? "ALL ALLIES RESCUED!" : savedCount >= 4 ? "RESCUE COMPLETE" : "MISSION INCOMPLETE";
    summaryText = path.finaleText(savedCount);
  } else if (path.id === "gamer") {
    headlineEmoji = gearUnlocked.length >= 6 ? "🐉" : "⚔️";
    headlineText = gearUnlocked.length >= 6 ? "DRAGON SLAYED!" : "QUEST INCOMPLETE";
    summaryText = path.finaleText(gearUnlocked.length);
  }

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", background: C.navy, position: "relative" }}>
      {/* Nav bar */}
      <div style={{
        background: C.navyMid,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        paddingTop: 10, paddingBottom: 10,
        borderBottom: `1px solid ${C.dim}`, flexShrink: 0,
        ...edgeStyle,
      }}>
        <button onClick={onRestart} style={{
          background: "transparent", border: "none",
          color: C.light, cursor: "pointer",
          display: "flex", alignItems: "center", gap: 6,
          padding: "4px 0", fontSize: 17, fontWeight: 700,
        }}>
          <span style={{ fontSize: 18 }}>🏠</span>
          <span>Home</span>
        </button>
        <button onClick={() => onShare()} style={{
          background: "transparent", border: "none",
          color: C.muted, cursor: "pointer",
          padding: "4px 0", display: "flex", alignItems: "center",
        }}>
          <ShareIcon size={22} color={C.muted} />
        </button>
      </div>

      {/* Hero */}
      <div style={{
        background: `linear-gradient(135deg, ${path.color}33, ${C.navyMid})`,
        paddingTop: 28, paddingBottom: 20, textAlign: "center",
        borderBottom: `1px solid ${path.color}44`,
        flexShrink: 0,
        ...edgeStyle,
      }}>
        <div style={{ fontSize: 44, marginBottom: 8 }}>{headlineEmoji}</div>
        <div style={{ fontSize: 22, fontWeight: 900, color: C.white, marginBottom: 4 }}>{headlineText}</div>
        <div style={{ fontSize: 12, color: path.color, letterSpacing: 1 }}>{subtitleText}</div>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, padding: 20, display: "flex", flexDirection: "column", gap: 14, overflow: "auto" }}>
        {/* Summary narrative */}
        <div style={{
          background: C.card, borderRadius: 10, padding: 16,
          border: `1px solid ${path.color}44`,
          fontSize: 13, color: C.light, lineHeight: 1.7, fontStyle: "italic",
        }}>
          {summaryText}
        </div>

        {/* Stats */}
        <div style={{
          background: C.card, borderRadius: 10, padding: 16,
          border: `1px solid ${C.dim}`,
        }}>
          <div style={{ fontSize: 11, color: C.muted, letterSpacing: 1, marginBottom: 12 }}>
            OPERATIONAL STATS
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 14 }}>
            {[
              { label: "Total XP", value: xpEarned, color: path.color },
              {
                label: path.id === "action" ? "Allies Saved" : path.id === "gamer" ? "Gear Earned" : "Checkpoints",
                value: path.id === "action" ? `${savedCount}/6` : path.id === "gamer" ? `${gearUnlocked.length}/6` : "6/6",
                color: C.teal,
              },
              {
                label: path.id === "action" ? "Total Time" : "Time",
                value: path.id === "action" && totalTime > 0 ? fmt(totalTime) : "~45 min",
                color: C.gold,
              },
            ].map(s => (
              <div key={s.label} style={{
                background: C.navyMid, borderRadius: 8, padding: "12px 8px", textAlign: "center",
              }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Path-specific detail row */}
          {path.id === "action" && (
            <div>
              <div style={{ fontSize: 10, color: C.muted, letterSpacing: 1, marginBottom: 8 }}>SQUAD OUTCOME</div>
              <div style={{ display: "flex", gap: 6 }}>
                {[
                  { name: "Reyes", emoji: "🧭" },
                  { name: "Mira",  emoji: "⚕️" },
                  { name: "Tomas", emoji: "🔧" },
                  { name: "Jin",   emoji: "💻" },
                  { name: "Dex",   emoji: "✈️" },
                  { name: "Atlas", emoji: "🎖️" },
                ].map((ally, i) => {
                  const saved = allyResults && allyResults[i];
                  return (
                    <div key={ally.name} style={{ flex: 1, textAlign: "center" }}>
                      <div style={{
                        width: "100%", aspectRatio: "1", borderRadius: "50%",
                        background: saved ? `${C.teal}22` : `${C.red}22`,
                        border: `2px solid ${saved ? C.teal : C.red}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 13, marginBottom: 3,
                      }}>
                        {saved ? ally.emoji : "✗"}
                      </div>
                      <div style={{ fontSize: 8, color: C.muted }}>{ally.name}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {path.id === "gamer" && (
            <div>
              <div style={{ fontSize: 10, color: C.muted, letterSpacing: 1, marginBottom: 8 }}>HERO LOADOUT</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[
                  { emoji: "🛡️", name: "Iron Shield" },
                  { emoji: "📜", name: "Ancient Scroll" },
                  { emoji: "🧥", name: "Stealth Cloak" },
                  { emoji: "🦅", name: "Wings of Ascension" },
                  { emoji: "⚡", name: "Power Surge" },
                  { emoji: "🗡️", name: "Dragon Sword" },
                ].map(g => {
                  const has = gearUnlocked.includes(g.name);
                  return (
                    <div key={g.name} title={g.name} style={{
                      width: 36, height: 36, borderRadius: 8,
                      background: has ? `${C.purple}33` : C.navyMid,
                      border: `2px solid ${has ? C.purple : C.dim}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: has ? 18 : 14, opacity: has ? 1 : 0.3,
                    }}>
                      {has ? g.emoji : "🔒"}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Next mission — chapter unlock */}
        {showNext && (() => {
          const nm = path.nextMission;
          const hookText = path.id === "action"
            ? path.nextMissionHook(savedCount)
            : path.id === "gamer"
            ? path.nextMissionHook(gearUnlocked.length)
            : path.nextMissionHook();
          return (
            <div style={{
              borderRadius: 12, overflow: "hidden",
              border: `2px solid ${path.color}`,
              background: `linear-gradient(135deg, ${path.color}18, ${C.card})`,
            }}>
              {/* Chapter header */}
              <div style={{
                background: `${C.gold}22`, padding: "8px 14px",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                borderBottom: `1px solid ${C.gold}33`,
              }}>
                <div style={{ fontSize: 12, color: C.gold, fontWeight: 800, letterSpacing: 0.5 }}>
                  🔓 {nm.chapter} UNLOCKED
                </div>
                <div style={{
                  background: C.pink, color: C.white, borderRadius: 4,
                  padding: "2px 8px", fontSize: 9, fontWeight: 800, letterSpacing: 0.5,
                }}>NEW</div>
              </div>

              <div style={{ padding: "14px 16px" }}>
                {/* Mission name */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                    background: `${path.color}22`, border: `1px solid ${path.color}44`,
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20,
                  }}>{nm.icon}</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: path.color }}>{nm.name}</div>
                </div>

                {/* Story teaser */}
                <div style={{
                  fontSize: 12, color: C.light, fontStyle: "italic",
                  lineHeight: 1.7, marginBottom: 12,
                  padding: "10px 12px",
                  background: `${C.navy}88`, borderRadius: 8,
                  borderLeft: `3px solid ${path.color}66`,
                }}>
                  "{nm.teaser}"
                </div>

                {/* Dynamic performance hook */}
                <div style={{
                  fontSize: 12, color: path.color, fontWeight: 600,
                  lineHeight: 1.5,
                }}>
                  ✦ {hookText}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Share & QR */}
        <div style={{
          background: `${C.pink}15`, borderRadius: 10, padding: 16,
          border: `1px solid ${C.pink}44`, textAlign: "center",
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.white, marginBottom: 4 }}>
            📲 Share your mission
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>
            Scan the QR code for your shareable recap card
          </div>
          {/* Deterministic QR tile */}
          <div style={{
            width: 64, height: 64, background: "#ffffff",
            margin: "0 auto 12px", borderRadius: 6,
            display: "grid", gridTemplateColumns: "repeat(6, 1fr)",
            gap: 2, padding: 6,
            border: "1px solid #ddd",
          }}>
            {QR_CELLS.map((on, i) => (
              <div key={i} style={{ background: on ? "#111111" : "transparent", borderRadius: 1 }} />
            ))}
          </div>
          {/* Share button */}
          <button onClick={() => onShare()} style={{
            background: "transparent", border: `1px solid ${C.pink}55`,
            borderRadius: 8, color: C.pink,
            padding: "8px 20px", fontSize: 13, fontWeight: 700,
            cursor: "pointer", display: "inline-flex", alignItems: "center",
            gap: 8,
          }}>
            <ShareIcon size={16} color={C.pink} /> Share
          </button>
        </div>
      </div>

      {/* Pinned footer — Book Session 2 */}
      <div style={{
        ...edgeStyle,
        background: path.color,
        paddingTop: 12, paddingBottom: 16,
        flexShrink: 0,
        display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
      }}>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.85)", fontWeight: 600 }}>
          15% off your next theme when you rebook today →
        </div>
        <a href="https://pursuitocr.com" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
          <button style={{
            background: "rgba(0,0,0,0.2)", color: "#fff",
            border: "1px solid rgba(255,255,255,0.3)",
            borderRadius: 8, width: "100%", padding: "12px 10px",
            fontWeight: 800, fontSize: 17, cursor: "pointer",
            letterSpacing: 0.5,
          }}>
            📅 BOOK SESSION 2 →
          </button>
        </a>
      </div>
    </div>
  );
}
